import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Collapse,
  IconButton,
  TextField
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { PointOfContact } from './forms/types/PointOfContact'
import DoneIcon from '@mui/icons-material/Done'

type Props = {
  data: PointOfContact | any
  onSubmit: (updated: any) => void
  onClear?: () => void
  disabled?: boolean
  required?: boolean
  refObj?: {
    firstName?: React.RefObject<HTMLInputElement>
  }
}

const PointOfContactEditor = ({
  data,
  onSubmit,
  required,
  onClear,
  disabled = false,
  refObj
}: Props) => {
  const [expanded, setExpanded] = useState(true)

  const formik = useFormik<PointOfContact>({
    initialValues: {
      person: {
        firstName: data?.person?.firstName || '',
        lastName: data?.person?.lastName || '',
        email: data?.person?.email || '',
        phone: data?.person?.phone || ''
      },
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      person: Yup.object().shape({
        firstName: Yup.string().required('Required'),
        lastName: Yup.string().required('Required'),
        email: Yup.string().email('Invalid email').required('Required'),
        phone: Yup.string().required('Required')
      })
    }),
    onSubmit: (values) => {
      onSubmit(values)
    }
  })
  // Auto-expand if required and empty on mount
  useEffect(() => {
    if (required && !data) {
      setExpanded(true)
    }
  }, [required, data])

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #d0d7de', // subtle grey border
          borderRadius: 2,
          p: 3,
          mt: 3,
          mb: 3,
          backgroundColor: '#f9fafb',
          width: '75%',
          maxWidth: '100%'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center">
            <Typography variant="h6">
              <span>Point of Contact</span>
            </Typography>
          </Box>
          <IconButton onClick={() => setExpanded(!expanded)} size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={6} md={6}>
              <TextField
                fullWidth
                label="First Name"
                inputRef={refObj?.firstName}
                name="person.firstName"
                autoComplete="off"
                value={formik.values.person.firstName}
                onChange={formik.handleChange}
                error={formik.touched.person?.firstName && Boolean(formik.errors.person?.firstName)}
                helperText={formik.touched.person?.firstName && formik.errors.person?.firstName}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="person.lastName"
                autoComplete="off"
                value={formik.values.person.lastName}
                onChange={formik.handleChange}
                error={formik.touched.person?.lastName && Boolean(formik.errors.person?.lastName)}
                helperText={formik.touched.person?.lastName && formik.errors.person?.lastName}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="person.email"
                autoComplete="off"
                value={formik.values.person.email}
                onChange={formik.handleChange}
                error={formik.touched.person?.email && Boolean(formik.errors.person?.email)}
                helperText={formik.touched.person?.email && formik.errors.person?.email}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <TextField
                fullWidth
                label="Phone"
                name="person.phone"
                autoComplete="off"
                value={formik.values.person.phone}
                onChange={formik.handleChange}
                error={formik.touched.person?.phone && Boolean(formik.errors.person?.phone)}
                helperText={formik.touched.person?.phone && formik.errors.person?.phone}
              />
            </Grid>
          </Grid>

          {!disabled && (
            <Box mt={3} display="flex" gap={2}>
              <Button
                variant="contained"
                color="success"
                type="button"
                onClick={() => {
                  formik.validateForm().then((errors) => {
                    if (Object.keys(errors).length === 0) {
                      onSubmit(formik.values)
                    } else {
                      formik.setTouched({
                        person: {
                          firstName: true,
                          lastName: true,
                          email: true,
                          phone: true
                        }
                      })
                    }
                  })
                }}
              >
                <DoneIcon sx={{ color: 'white' }} />
              </Button>
            </Box>
          )}
        </Collapse>
        {required && (
          <Box mt={2}>
            <Typography variant="body2" color="error">
              Please complete required contact details
            </Typography>
          </Box>
        )}
      </Paper>
    </>
  )
}

export default PointOfContactEditor
